function camelToSnake (str) {
  return str.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
}

function camelKeyToSnakeKey (obj) {
  const ret = {};
  for (const key in obj) {
    ret[camelToSnake(key)] = obj[key];
  }
  return ret;
}

function camelKeyToSnakeKeyRecursive (obj) {
  if (!!obj && obj.constructor === Object) {
    const ret = camelKeyToSnakeKey(obj);
    for (const key in obj) {
      ret[camelToSnake(key)] = camelKeyToSnakeKeyRecursive(obj[key])
    }
    return ret;
  } else {
    return obj;
  }
}

function getBytesWithUnit (
  bytes, delimiter = '', precision = 2, units = ['B', 'KB', 'MB', 'GB', 'TB'],
) {
  if (bytes < 0) {
    throw new Error('File size cannot be negative.');
  }
  const max_bytes = Math.pow(1024, units.length) - 1;
  if (bytes > max_bytes) {
    throw new Error('File size is too large.');
  }

  if (bytes === 0) {
    return `0${delimiter}${units[0]}`;
  }
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const value = (bytes / Math.pow(1024, i)).toFixed(precision);
  return `${value}${delimiter}${units[i]}`;
}

function getDisplayedUrl (url) {
  return url.replace(/^(?:https?:\/\/)?(?:http?:\/\/)?(?:www\.)?/i, '');
}

function generateRandomPassword(passwordLength = 16) {
  const numbers = '1234567890';
  const alphabets = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const specialCharacters = '!@#$%^&*';
  const letterSets = [numbers, alphabets, specialCharacters];

  const password = [...Array(passwordLength).keys()].reduce((results, elem) => {
    const letterSetIndex = Math.floor(Math.random() * letterSets.length);
    const letterSet = letterSets[letterSetIndex];
    const letterIndex = Math.floor(Math.random() * letterSet.length);
    results.push(letterSet[letterIndex]);
    return results;
  }, []);

  return password.join('');
}

function isEquivalentSkillName (name1, name2) {
  const name1_ = normalizeSkillName(name1).toLowerCase();
  const name2_ = normalizeSkillName(name2).toLowerCase();
  return name1_ === name2_;
}

/**
 * Convert a string in kebab-case (dash-delimited) to camelCase.
 */
function kebabToCamel(str) {
  return str
    .trim()
    .replace(/-+$/, '')
    .replace(/-([a-z])/g, (_, char) => (char ? char.toUpperCase() : ''));
}

function camelToKebab(str) {
  return str
    .replace(/([a-z])([A-Z])/g, '$1-$2')
    .toLowerCase();
}

function collapseWhitespace (str) {
  return str.replace(/\s+/g, ' ');
}

function normalizeWhitespaces (str) {
  return str.replace(/\s+/g, ' ').trim();
}

function normalizeSkillName (name) {
  return normalizeWhitespaces(toHalfWidth(name));
}

function queryParamsInt(label, params) {
  const queryParams = params.map((param) => {
    return `${label}=${param.toString()}`
  });
  return queryParams.join('&');
}

function snakeToCamel (str) {
  return str.replace(/([_][a-z])/g, letter => letter.toUpperCase().replace('_', ''));
}

function snakeKeyToCamelKey (obj) {
  const ret = {};
  for (const key in obj) {
    ret[snakeToCamel(key)] = obj[key];
  }
  return ret;
}

function snakeKeyToCamelKeyRecursive (obj) {
  if (!!obj && obj.constructor === Object) {
    const ret = snakeKeyToCamelKey(obj);
    for (const key in obj) {
      ret[snakeToCamel(key)] = snakeKeyToCamelKeyRecursive(obj[key])
    }
    return ret;
  } else if (!!obj && Array.isArray(obj)) {
    return obj.reduce((result, elem) => {
      result.push(snakeKeyToCamelKeyRecursive(elem));
      return result;
    }, []);
  } else {
    return obj;
  }
}

function splitString(src, separator, caseSensitive = false) {
  const arr = caseSensitive ? src.split(separator) : src.toLowerCase().split(separator.toLowerCase());

  if (arr.length === 1) { return ['', src, '']; }

  const startIndex = arr[0].length;
  const endIndex = arr[1].length > 0 ? -arr[1].length : src.length;

  const head = src.slice(0, startIndex).replace(/ /g, '\u00a0');
  const body = src.slice(startIndex, endIndex).replace(/ /g, '\u00a0');
  const tail = src.slice(endIndex, src.length).replace(/ /g, '\u00a0');

  return [head, body, tail];
}

function toHalfWidth(str) {
  /*
  !!!Caution!!!
  Do not delete comment("eslint-disable-next-line") in below,
  to avoid eslint error detecting full-width char/irregular whitespace.
  */
  return str.replace(
    /[！-～]/g, (elem) => String.fromCharCode(elem.charCodeAt(0) - 0xFEE0))
    .replace(/”/g, "\"")
    .replace(/’/g, "'")
    .replace(/‘/g, "`")
    .replace(/￥/g, "\\") // eslint-disable-next-line
    .replace(/　/g, " ")
    .replace(/〜/g, "~");
}

function truncate(str, maxLength, ellipsis = '...') {
  return str.length > maxLength ? str.slice(0, maxLength - ellipsis.length) + ellipsis : str;
}

function validateEmail (email) {
  return String(email)
    .toLowerCase()
    .match(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    );
}

export {
  camelToKebab,
  camelToSnake,
  camelKeyToSnakeKey,
  camelKeyToSnakeKeyRecursive,
  collapseWhitespace,
  getBytesWithUnit,
  getDisplayedUrl,
  generateRandomPassword,
  isEquivalentSkillName,
  kebabToCamel,
  normalizeSkillName,
  normalizeWhitespaces,
  queryParamsInt,
  snakeToCamel,
  snakeKeyToCamelKey,
  snakeKeyToCamelKeyRecursive,
  splitString,
  toHalfWidth,
  truncate,
  validateEmail,
};
